/** India §56(2)(x) gift totals for the financial year, in INR. */
export interface GiftTotals {
    relative_total: number;      // exempt at any amount
    relative_count: number;
    non_relative_total: number;  // tested against the limit
    non_relative_count: number;
    exempt_total?: number;       // marriage / will or inheritance — never income
    exempt_count?: number;
    exemption_limit: number;     // ₹50,000
    taxable: number;             // non_relative_total once it exceeds the limit, else 0
    missingRateDates: string[];
}

export interface TaxSummary {
    tax_year: number;
    currency: string;
    // Capital gains — split by holding period
    st_gains: number;
    st_losses: number;
    st_net: number;
    lt_gains: number;
    lt_losses: number;
    lt_net: number;
    net_capital_gain: number;
    // Ordinary income
    staking_income: number;
    airdrop_income: number;
    other_income: number;
    // India only (absent elsewhere): §56(2)(x) gift income, already inside total_income.
    gift_income?: number;
    gifts?: GiftTotals | null;
    total_income: number;
    // Fees — `fees_deducted` says whether they were taken off the gains. India's §115BBH allows
    // only the cost of acquisition, so there they are paid but reduce nothing.
    total_fees: number;
    fees_deducted: boolean;
    // Aggregated taxable amount
    total_taxable: number;
    // Counts
    disposal_count: number;
    income_count: number;
    total_count: number;
    // Estimate — the same fields the Transactions stats endpoint returns, so both screens render
    // one card from one set of numbers.
    estimated_tax: number;
    estimate_label: string;
    /** The country's own working, sent alongside the figure — a heading plus one line per rule
     * (flat rate, loss treatment, fee treatment, cess, what the estimate excludes). The server has
     * always sent these; the card renders them in its tooltip so the single `estimate_label` line
     * is not left standing in for rules it does not cover. */
    estimate_calc_note: string;
    estimate_rate_lines: string[];
    estimate_losses_ignored: number;
    estimate_gains_tax: number;
    estimate_income_tax: number;
    estimate_cess: number;
}

export interface CapitalGainTerm {
    term: 'short' | 'long';
    proceeds: number;
    cost_basis: number;
    fees: number;
    gain_loss: number;
    qty: number;
    tx_count: number;
}

export interface CapitalGainsByAsset {
    asset_symbol: string;
    total_proceeds: number;
    total_cost_basis: number;
    total_gain_loss: number;
    total_qty: number;
    terms: CapitalGainTerm[];
}

export interface TaxSummaryApiResponse {
    data: TaxSummary;
    metadata?: { message?: string; statusCode?: number };
}

// ─── Crypto tax engine (multi-country) ──────────────────────────────────────
export type TaxCountry = 'US' | 'AU' | 'DE' | 'IN' | 'SG' | 'IE' | 'CA' | 'UK';

const TAX_COUNTRY_ALIASES: Record<string, TaxCountry> = { GB: 'UK' };
const ALL_TAX_COUNTRIES: readonly TaxCountry[] = ['US', 'AU', 'DE', 'IN', 'SG', 'IE', 'CA', 'UK'];


export function toTaxCountry(code: unknown): TaxCountry | null {
    if (typeof code !== 'string') return null;
    const upper = code.trim().toUpperCase();
    const resolved = TAX_COUNTRY_ALIASES[upper] ?? upper;
    return ALL_TAX_COUNTRIES.includes(resolved as TaxCountry) ? (resolved as TaxCountry) : null;
}
export type FilingStatus =
    | 'single'
    | 'married_filing_jointly'
    | 'married_filing_separately'
    | 'individual'
    | 'married'
    | 'new_regime'
    | 'old_regime'
    // Singapore & Canada: no joint filing — the slot carries the investor-vs-trader determination.
    | 'investor'
    | 'trader';

// Which downloadable filing reports each country has built. The UI hides/disables a
// download button when its report isn't built, instead of firing a request that 403s.
export interface ReportCapabilities {
    pdf: boolean;
    csv: boolean;
    itemizedPdf?: boolean;
}

export interface CryptoTaxOptions {
    resolvedCountry: TaxCountry | null;
    needsCountryOverride: boolean;
    rawCountry: string | null;
    filingStatusesByCountry: Record<TaxCountry, FilingStatus[]>;
    supportedYearsByCountry: Record<TaxCountry, number[]>;
    supportedCountries: TaxCountry[];
    // Per-country {pdf, csv} download availability. Optional so an older backend response
    // (without the field) degrades gracefully — a missing entry is treated as "built".
    reportCapabilitiesByCountry?: Record<TaxCountry, ReportCapabilities>;
}

// Statuses assessed on the couple's combined income (US married filing jointly,
// DE married). For these the UI collects a separate spouse income that the backend
// sums onto ordinaryIncome.
export const JOINT_FILING_STATUSES: readonly FilingStatus[] = [
    'married_filing_jointly',
    'married',
];

export function isJointFilingStatus(status: FilingStatus | null): boolean {
    return status !== null && JOINT_FILING_STATUSES.includes(status);
}

export interface EstimateRequest {
    taxYear: number;
    filingStatus: FilingStatus;
    ordinaryIncome: number;
    spouseIncome?: number;
    country?: TaxCountry;
    // India only: opt in to also computing slab tax on the user's salary (combined total).
    includeOrdinaryIncome?: boolean;
    // India only: 1% §194S TDS already deducted, credited against the total.
    tdsPaid?: number;
}
export interface InVdaSummary {
    regime: 'new_regime' | 'old_regime';
    vdaGains: number; // sum of positive disposals (no netting)
    lossesIgnored: number; // losses neither offset nor carried forward
    flatRate: number; // 0.30
    flatTax: number;
    surchargeRate: number;
    surcharge: number;
    cessRate: number; // 0.04
    cess: number;
    totalTax: number;
}

// India slab-rate view for crypto RECEIPTS (staking / airdrops / mining). Unlike disposals,
// these are ordinary income taxed at the user's slab rate at the time of receipt, stacked on
// top of salary (marginal-difference method). Mirrors InIncomeSummary on the backend.
export interface InIncomeSummary {
    cryptoIncome: number; // staking/airdrop/mining receipts taxed as ordinary income
    salaryBase: number; // salary after the standard deduction (the stacking base)
    standardDeduction: number;
    slabTax: number; // marginal slab tax on the receipts
    rebateApplied: boolean; // whether §87A reduced/zeroed the slab tax
    surchargeRate: number;
    surcharge: number;
    cessRate: number; // 0.04
    cess: number;
    totalTax: number; // slabTax + surcharge + cess
}

// India slab tax on the user's OWN salary, computed only when they opt in (the "include my
// ordinary income" checkbox). Mirrors InSalaryTaxSummary on the backend.
export interface InSalaryTaxSummary {
    taxableIncome: number; // salary after the standard deduction
    slabTax: number; // after §87A rebate + marginal relief
    rebateApplied: number; // §87A rebate amount
    marginalRelief: number; // relief amount just above the rebate ceiling
    cessRate: number; // 0.04
    cess: number;
    totalTax: number; // slabTax + cess
}

export interface TaxBreakdown {
    country: TaxCountry;
    taxYear: number;
    netShortTermGain: number;
    netLongTermGain: number;
    taxableShortTermGain: number;
    taxableLongTermGain: number;
    shortTermTax: number;
    longTermTax: number;
    surcharge: number; // NIIT (US) / Medicare Levy (AU) / Soli (DE) / surcharge + 4% cess (IN)
    // Uniform crypto-receipt income line for EVERY country (staking / airdrop / mining /
    // crypto-salary). cryptoIncome is the FMV on receipt; cryptoIncomeTax is the income tax
    // attributable to it (already inside totalCryptoTax). Present for all jurisdictions.
    cryptoIncome?: number;
    cryptoIncomeTax?: number;
    totalCryptoTax: number;
    effectiveRate: number;
    notes: string[];
    vda?: InVdaSummary; // India only
    income?: InIncomeSummary; // India only — slab tax on staking/airdrop receipts
    salary?: InSalaryTaxSummary; // India only — present when "include my income" is on
    tdsCredit?: number; // India only — 1% §194S already paid, credited against the total
    grandTotal?: number; // India only — crypto + (salary, if included) − TDS (may be < 0 = refund)
}

export interface CryptoTaxOptionsApiResponse {
    data: CryptoTaxOptions;
    metadata?: { message?: string; statusCode?: number };
}

export interface TaxBreakdownApiResponse {
    data: TaxBreakdown;
    metadata?: { message?: string; statusCode?: number };
}

export interface CapitalGainsPagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface CapitalGainsTotals {
    total_proceeds: number;
    total_cost_basis: number;
    total_gain_loss: number;
}

export interface CapitalGainsApiResponse {
    data: CapitalGainsByAsset[];
    pagination?: CapitalGainsPagination;
    totals?: CapitalGainsTotals;
    metadata?: { message?: string; statusCode?: number };
}

// ─── Upcoming tax events (dashboard calendar card) ──────────────────────────
export interface UpcomingTaxEvent {
    id: string;
    title: string;
    description: string;
    isoDate: string; // 'YYYY-MM-DD' — next upcoming occurrence
    type: 'deadline' | 'reminder';
}

export interface UpcomingTaxEvents {
    resolvedCountry: TaxCountry | null;
    supportedCountries: TaxCountry[];
    eventsByCountry: Record<TaxCountry, UpcomingTaxEvent[]>;
}

export interface UpcomingTaxEventsApiResponse {
    data: UpcomingTaxEvents;
    metadata?: { message?: string; statusCode?: number };
}
